/**
 * Authentication Methods Configuration Component
 *
 * Displays and manages all 9 Azure AD authentication method configurations:
 * - SMS
 * - Email OTP
 * - FIDO2 Security Keys
 * - Voice Call
 * - Software OATH Tokens
 * - Hardware OATH Tokens
 * - X.509 Certificate
 * - Microsoft Authenticator (NEW - with 5 Azure Portal features)
 * - Temporary Access Pass (NEW - with 4 configuration options)
 *
 * Provides list view with enable/disable status and edit functionality.
 * Microsoft Authenticator and TAP use standalone card components.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Spinner,
  Button,
  Switch,
  Checkbox,
} from '@fluentui/react-components';
import {
  ChatRegular,
  MailRegular,
  ShieldKeyholeRegular,
  CallRegular,
  AppsRegular,
  KeyRegular,
  CertificateRegular,
  PhoneDesktopRegular,
  PasswordRegular,
  WindowMultipleRegular,
  ShieldRegular,
} from '@fluentui/react-icons';
import { useAuthenticationMethods } from '../../../hooks/useAuthenticationMethods';
import { Alert } from '../../common/Alert';
import { SecurityStrengthIndicator } from '../../SelfService/SecurityStrengthIndicator';
import { MicrosoftAuthenticatorCard } from './MicrosoftAuthenticatorCard';
import { TemporaryAccessPassCard } from './TemporaryAccessPassCard';
import {
  AUTHENTICATION_METHODS,
  type AuthenticationMethodKey,
  type AuthenticationMethodConfig,
} from '../../../types/authenticationMethods';

/**
 * Get icon for authentication method type
 */
const getMethodIcon = (methodKey: string): JSX.Element => {
  const iconMap: Record<string, JSX.Element> = {
    sms: <ChatRegular />,
    email: <MailRegular />,
    fido2: <ShieldKeyholeRegular />,
    voice: <CallRegular />,
    software_oath: <AppsRegular />,
    hardware_oath: <KeyRegular />,
    x509_certificate: <CertificateRegular />,
    microsoft_authenticator: <PhoneDesktopRegular />,
    temporary_access_pass: <PasswordRegular />,
    windows_hello: <WindowMultipleRegular />,
  };
  return iconMap[methodKey] || <ShieldRegular />;
};

/**
 * Get description for authentication method type
 */
const getMethodDescription = (methodKey: string): string => {
  const descMap: Record<string, string> = {
    sms: 'Receive verification codes via text message',
    email: 'Receive verification codes via email',
    fido2: 'Sign in using physical hardware security keys',
    voice: 'Receive verification codes via phone call',
    software_oath: 'Generate time-based codes using authenticator apps',
    hardware_oath: 'Generate time-based codes using hardware tokens',
    x509_certificate: 'Sign in using X.509 certificate authentication',
    microsoft_authenticator: 'Approve sign-ins via push notifications',
    temporary_access_pass: 'Temporary one-time passwords for user onboarding',
    windows_hello: 'Sign in with Windows Hello biometrics',
  };
  return descMap[methodKey] || '';
};

/**
 * Get security level for authentication method based on Microsoft Entra official ratings (1-3)
 * Reference: https://learn.microsoft.com/en-us/entra/identity/authentication/how-to-authentication-methods-manage
 * - Level 3 (High): Passkey (FIDO2), Temporary Access Pass, Microsoft Authenticator, Windows Hello, X.509 Certificate
 * - Level 2 (Medium): Hardware OATH tokens, Software OATH tokens
 * - Level 1 (Low): Email OTP, SMS, Voice call
 */
const getSecurityLevel = (methodKey: string): number => {
  // Level 3: High security
  if (['fido2', 'temporary_access_pass', 'microsoft_authenticator', 'windows_hello', 'x509_certificate'].includes(methodKey)) return 3;
  // Level 2: Medium security
  if (['hardware_oath', 'software_oath'].includes(methodKey)) return 2;
  // Level 1: Low security
  return 1; // email, sms, voice
};

/**
 * Get security text for security level based on Microsoft Entra official ratings
 */
const getSecurityText = (level: number): string => {
  const textMap: Record<number, string> = {
    3: 'High security',
    2: 'Medium security',
    1: 'Low security',
  };
  return textMap[level] || 'Unknown';
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXL),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  // Card-based styles (matching MFAMethodList pattern)
  methodCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    ...shorthands.borderWidth('2px'),
    ...shorthands.borderStyle('solid'),
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    transitionProperty: 'border-color',
    transitionDuration: '0.2s',
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
    },
  },
  methodHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
  },
  methodIcon: {
    fontSize: '48px',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
    '& > svg': {
      width: '48px',
      height: '48px',
    },
  },
  methodInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    flex: 1,
  },
  methodHeaderControls: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  methodTitleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  methodType: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  methodDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  methodControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  saveButton: {
    alignSelf: 'flex-start',
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL),
    color: tokens.colorNeutralForeground3,
  },
});

interface AuthenticationMethodsConfigProps {
  tenantId: string;
}

interface MethodRow {
  methodKey: AuthenticationMethodKey;
  name: string;
  state: 'enabled' | 'disabled';
  configuration: Record<string, unknown>;
}

export const AuthenticationMethodsConfig: React.FC<AuthenticationMethodsConfigProps> = ({
  tenantId,
}) => {
  const styles = useStyles();

  // Use React Query hook for data fetching and mutations
  const {
    methods: rawMethods,
    isLoading,
    error: fetchError,
    updateMethod,
  } = useAuthenticationMethods(tenantId);

  // Local UI state
  const [success, setSuccess] = useState<string | null>(null);

  // Inline editing state
  const [editingMethodKey, setEditingMethodKey] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<AuthenticationMethodConfig | null>(null);
  const [savingMethodKey, setSavingMethodKey] = useState<string | null>(null);

  // Derive separated configs from raw methods
  const { inlineMethods, authenticatorConfig, tapConfig } = useMemo(() => {
    if (!rawMethods) {
      return { inlineMethods: [], authenticatorConfig: undefined, tapConfig: undefined };
    }

    const inlineMethodsList: MethodRow[] = [];
    let authenticatorConf: Record<string, unknown> | undefined;
    let tapConf: Record<string, unknown> | undefined;

    for (const config of rawMethods) {
      if (config.methodKey === 'microsoft_authenticator') {
        authenticatorConf = { state: config.state, configuration: config.configuration };
      } else if (config.methodKey === 'temporary_access_pass') {
        tapConf = { state: config.state, configuration: config.configuration };
      } else {
        const key = config.methodKey as AuthenticationMethodKey;
        inlineMethodsList.push({
          methodKey: key,
          name: AUTHENTICATION_METHODS[key],
          state: config.state,
          configuration: config.configuration,
        });
      }
    }

    // Sort inline methods by security level (highest to lowest)
    inlineMethodsList.sort((a, b) => {
      const levelA = getSecurityLevel(a.methodKey);
      const levelB = getSecurityLevel(b.methodKey);
      return levelB - levelA; // Descending order (3 → 2 → 1)
    });

    return { inlineMethods: inlineMethodsList, authenticatorConfig: authenticatorConf, tapConfig: tapConf };
  }, [rawMethods]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSaveMethod = async (methodKey: string) => {
    if (!editingConfig) return;

    setSavingMethodKey(methodKey);

    try {
      await updateMethod({
        methodKey,
        state: editingConfig.state,
        configuration: editingConfig.configuration,
      });

      setSuccess(`${AUTHENTICATION_METHODS[methodKey as AuthenticationMethodKey]} updated successfully`);
      setEditingMethodKey(null);
      setEditingConfig(null);
    } catch {
      // Error is captured in hook's updateError
    } finally {
      setSavingMethodKey(null);
    }
  };

  // Handler for Microsoft Authenticator card changes
  const handleAuthenticatorConfigChange = async (config: Record<string, unknown>) => {
    try {
      await updateMethod({
        methodKey: 'microsoft_authenticator',
        state: config.state as 'enabled' | 'disabled',
        configuration: config.configuration as Record<string, unknown>,
      });

      setSuccess('Microsoft Authenticator updated successfully');
    } catch {
      // Error is captured in hook's updateError
    }
  };

  // Handler for TAP card changes
  const handleTapConfigChange = async (config: Record<string, unknown>) => {
    try {
      await updateMethod({
        methodKey: 'temporary_access_pass',
        state: config.state as 'enabled' | 'disabled',
        configuration: config.configuration as Record<string, unknown>,
      });

      setSuccess('Temporary Access Pass updated successfully');
    } catch {
      // Error is captured in hook's updateError
    }
  };

  if (!tenantId) {
    return (
      <Alert intent="warning" title="No Tenant Selected">
        Please select a tenant to view authentication methods.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner
          label="Loading authentication methods..."
          role="progressbar"
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {fetchError && (
        <Alert intent="error" title="Error">
          Failed to load authentication methods: {fetchError.message}
        </Alert>
      )}

      {success && (
        <Alert intent="success" title="Success">
          {success}
        </Alert>
      )}

      <div className={styles.cardContent}>
          {/* Standalone Cards: Microsoft Authenticator */}
          {authenticatorConfig && (
            <MicrosoftAuthenticatorCard
              tenantId={tenantId}
              initialConfig={authenticatorConfig}
              onConfigChange={handleAuthenticatorConfigChange}
            />
          )}

          {/* Inline Edit Methods (SMS, Email, FIDO2, Voice, etc.) */}
          {inlineMethods.length === 0 ? (
            <div className={styles.emptyState}>
              <Text>No authentication methods found.</Text>
            </div>
          ) : (
            inlineMethods.map((method) => {
              const isEditing = editingMethodKey === method.methodKey;
              const isSaving = savingMethodKey === method.methodKey;
              const securityLevel = getSecurityLevel(method.methodKey);

              return (
                <>
                  <article key={method.methodKey} className={styles.methodCard}>
                    {/* Header Section */}
                    <div className={styles.methodHeader}>
                      {/* Icon */}
                      <div className={styles.methodIcon}>
                        {getMethodIcon(method.methodKey)}
                      </div>

                      {/* Info Section */}
                      <div className={styles.methodInfo}>
                        <div className={styles.methodTitleSection}>
                          <Text className={styles.methodType}>{method.name}</Text>
                          <Text className={styles.methodDescription}>
                            {getMethodDescription(method.methodKey)}
                          </Text>
                        </div>
                        <SecurityStrengthIndicator
                          level={securityLevel}
                          securityText={getSecurityText(securityLevel)}
                        />
                      </div>

                      {/* Switch Control on Right Side */}
                      <div className={styles.methodHeaderControls}>
                        <Switch
                          checked={isEditing ? editingConfig?.state === 'enabled' : method.state === 'enabled'}
                          onChange={(_, data) => {
                            if (!isEditing) {
                              setEditingMethodKey(method.methodKey);
                              setEditingConfig({
                                methodKey: method.methodKey,
                                state: data.checked ? 'enabled' : 'disabled',
                                configuration: method.configuration,
                              });
                            } else {
                              setEditingConfig({
                                ...editingConfig!,
                                state: data.checked ? 'enabled' : 'disabled',
                              });
                            }
                          }}
                          label={isEditing ? (editingConfig?.state === 'enabled' ? 'Enabled' : 'Disabled') : (method.state === 'enabled' ? 'Enabled' : 'Disabled')}
                          disabled={isSaving}
                        />
                      </div>
                    </div>

                    {/* Additional Edit Controls (FIDO2 checkbox and Save button) */}
                    {isEditing && (
                      <div className={styles.methodControls}>
                        {/* FIDO2 Self-Service Registration Option */}
                        {method.methodKey === 'fido2' && (
                          <div className={styles.controlRow}>
                            <Checkbox
                              checked={editingConfig?.configuration?.isSelfServiceRegistrationAllowed === true}
                              onChange={(_, data) => {
                                setEditingConfig({
                                  ...editingConfig!,
                                  configuration: {
                                    ...editingConfig!.configuration,
                                    isSelfServiceRegistrationAllowed: data.checked,
                                  },
                                });
                              }}
                              label="Allow self-service registration"
                              disabled={isSaving}
                            />
                          </div>
                        )}

                        {/* Save Button */}
                        <Button
                          appearance="primary"
                          onClick={() => handleSaveMethod(method.methodKey)}
                          disabled={isSaving}
                          className={styles.saveButton}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    )}
                  </article>

                  {/* Insert TAP card after hardware_oath */}
                  {method.methodKey === 'hardware_oath' && tapConfig && (
                    <TemporaryAccessPassCard
                      key="temporary_access_pass"
                      tenantId={tenantId}
                      initialConfig={tapConfig}
                      onConfigChange={handleTapConfigChange}
                    />
                  )}
                </>
              );
            })
          )}
      </div>
    </div>
  );
};
